import React, { useState, useEffect } from 'react';
import { 
  PencilIcon, 
  TrashIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import documentTemplateService, {
  DocumentTemplateWithUsage,
  UpdateDocumentTemplateRequest
} from '../../../../../../services/documentTemplateService';
import { TemplateBuilder } from '../../../../settings/documentTemplates/TemplateBuilder';
import { useTemplateStore } from '../../../../settings/documentTemplates/templateStore';
import { listTemplates } from '../../../../../dms/templates/TemplateRegistry';

// Using DocumentTemplateWithUsage from service
type DocumentTemplate = DocumentTemplateWithUsage;



interface DocumentTemplatesProps {
  className?: string;
}

interface TemplateFormData {
  name: string;
  description: string;
  template_type: 'contract' | 'pleading' | 'correspondence' | 'form' | 'research' | 'discovery' | 'other';
  practice_areas: string[];
  file_extensions: string[];
  is_enabled: boolean;
}

// Using DocumentTemplateWithUsage which already includes usage_count, can_edit, can_delete
type TemplateWithDocumentCount = DocumentTemplateWithUsage;

export const DocumentTemplates: React.FC<DocumentTemplatesProps> = ({ className = '' }) => {
  const [templates, setTemplates] = useState<TemplateWithDocumentCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    template_type: 'other',
    practice_areas: [],
    file_extensions: [],
    is_enabled: true
  });
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const templateStore = useTemplateStore();

  // Real API data - no more mock data

  // Load templates with document counts
  const loadTemplatesWithCounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await documentTemplateService.getTemplates({
        include_usage_count: true
      });
      setTemplates(response.templates);
    } catch (err) {
      console.error('Failed to load templates with document counts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load template usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplatesWithCounts();
  }, []);

  function openTemplateBuilderFromBaseline() {
    try {
      templateStore.loadInitial();
      const baselines = listTemplates();
      const caBaseline = baselines.find((b) => b.id === 'CA_MOTION_PLEADING_BASELINE_V1') || baselines[0];
      if (caBaseline) {
        templateStore.createFromBaseline(caBaseline);
        setShowBuilder(true);
      }
    } catch (e) {
      console.error('Failed to open Template Builder:', e);
    }
  }

  // Refresh templates
  const refreshTemplates = async () => {
    await loadTemplatesWithCounts();
  };

  // Update existing template (restricted based on document usage)
  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    try {
      setLoading(true);
      setError(null);

      const updateData: UpdateDocumentTemplateRequest = {
        name: formData.name,
        description: formData.description,
        template_type: formData.template_type,
        practice_areas: formData.practice_areas,
        file_extensions: formData.file_extensions,
        is_enabled: formData.is_enabled
      };

      await documentTemplateService.updateTemplate(
        editingTemplate.template_id,
        updateData
      );

      // Refresh templates list
      await loadTemplatesWithCounts();
      
      setEditingTemplate(null);
      resetForm();
    } catch (err) {
      console.error('Failed to update template:', err);
      setError(err instanceof Error ? err.message : 'Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId: string) => {
    const template = templates.find(t => t.template_id === templateId);
    if (!template) return;

    if (template.usage_count > 0) {
      setDeleteMessage(`Cannot delete template "${template.name}". There are ${template.usage_count} documents using this template.`);
      return;
    }

    try {
      setDeleteLoading(templateId);
      setError(null);
      setDeleteMessage(null);

      await documentTemplateService.deleteTemplate(templateId);
      
      // Refresh templates list
      await loadTemplatesWithCounts();
      setDeleteMessage(`Template "${template.name}" deleted successfully.`);
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Start editing template
  const startEditing = (template: DocumentTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      template_type: template.template_type,
      practice_areas: template.practice_areas || [],
      file_extensions: template.file_extensions || [],
      is_enabled: template.is_enabled
    });
    setError(null);
    setDeleteMessage(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingTemplate(null);
    resetForm();
    setError(null);
    setDeleteMessage(null);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_type: 'other',
      practice_areas: [],
      file_extensions: [],
      is_enabled: true
    });
  };

  const getTemplateTypeIcon = (type: string) => {
    switch (type) {
      case 'contract': return <DocumentDuplicateIcon className="h-4 w-4 text-blue-600" />;
      case 'pleading': return <DocumentDuplicateIcon className="h-4 w-4 text-purple-600" />;
      case 'correspondence': return <DocumentDuplicateIcon className="h-4 w-4 text-green-600" />;
      case 'form': return <DocumentDuplicateIcon className="h-4 w-4 text-yellow-600" />;
      case 'research': return <DocumentDuplicateIcon className="h-4 w-4 text-indigo-600" />;
      case 'discovery': return <DocumentDuplicateIcon className="h-4 w-4 text-orange-600" />;
      default: return <DocumentDuplicateIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTemplateTypeColor = (type: string) => {
    switch (type) {
      case 'contract': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'pleading': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'correspondence': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'form': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'research': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300';
      case 'discovery': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading document templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between text-gray-900 dark:text-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FolderIcon className="h-6 w-6 text-lex-accent mr-2" />
            Document Templates
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage document templates used across your firm
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Refresh button */}
          <button
            onClick={refreshTemplates}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Refresh"
          >
            Refresh
          </button>
          <button
            onClick={openTemplateBuilderFromBaseline}
            className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover"
            title="Open Template Builder"
          >
            New Template (Builder)
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {deleteMessage && (
        <div className={`border rounded-md p-4 ${
          deleteMessage.includes('Cannot delete') 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}>
          <div className="flex">
            {deleteMessage.includes('Cannot delete') ? (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            )}
            <p className={`text-sm ${
              deleteMessage.includes('Cannot delete')
                ? 'text-red-700 dark:text-red-300'
                : 'text-green-700 dark:text-green-300'
            }`}>
              {deleteMessage}
            </p>
          </div>
        </div>
      )}

      {/* Edit Mode Notice */}
      {editingTemplate && (
        <div className="bg-lex-accent-soft border border-lex-accent/30 rounded-md p-4">
          <div className="flex">
            <DocumentDuplicateIcon className="h-5 w-5 text-lex-accent mr-2" />
            <div className="text-sm text-lex-accent">
              <p className="font-medium">Editing Template: {editingTemplate.name}</p>
              {templates.find(t => t.template_id === editingTemplate.template_id)?.usage_count! > 0 && (
                <p className="mt-1">
                  <strong>Note:</strong> This template is in use by documents. Only name, description, and status can be modified.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Templates Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Document Templates</h4>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Documents Using
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {templates.map((template) => (
                <tr key={template.template_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getTemplateTypeIcon(template.template_type)}
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {template.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {template.description || 'No description'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTemplateTypeColor(template.template_type)}`}>
                      {template.template_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {template.usage_count}
                      </span>
                      {template.usage_count > 0 && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold bg-lex-accent-soft text-lex-accent rounded-full">
                          In Use
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      template.is_enabled 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                    }`}>
                      {template.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => startEditing(template)}
                      className="text-lex-accent hover:text-lex-accent-hover"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.template_id)}
                      disabled={deleteLoading === template.template_id || template.usage_count > 0}
                      className={`${
                        template.usage_count > 0
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                      }`}
                      title={template.usage_count > 0 ? `Cannot delete. There are ${template.usage_count} documents using this template.` : 'Delete template'}
                    >
                      {deleteLoading === template.template_id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showBuilder && (
        <div className="fixed inset-0 z-[200]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBuilder(false)} />
          <div className="absolute inset-6 bg-white rounded shadow-xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <div className="text-sm font-semibold">Template Builder</div>
              <button className="px-2 py-1 text-sm" onClick={() => setShowBuilder(false)}>✕</button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <TemplateBuilder />
            </div>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingTemplate && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              Edit Template: {editingTemplate.name}
            </h4>
            <div className="space-x-2">
              <button
                onClick={handleUpdateTemplate}
                disabled={loading}
                className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={cancelEditing}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                placeholder="Enter template name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.is_enabled ? 'enabled' : 'disabled'}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.value === 'enabled' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                placeholder="Enter template description"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTemplates;